import {
  computed,
  getCurrentInstance,
  inject,
  InjectionKey,
  nextTick,
  onScopeDispose,
  provide,
  ref,
  shallowRef,
  watch,
} from 'vue';
import CatalogV2Service, {
  catalogErrorCode,
} from '@/api/services/catalog-v2.service';
import { useConfirmDialog } from '@/composition/use-confirm-dialog';
import { useMenuStore } from '@/composition/use-menu';
import { useUi } from '@/composition/use-ui';
import {
  changedDuringSave,
  changedKeys,
  createPayloadFromForm,
  detachMissingSizes,
  diffProductForm,
  emptyProductForm,
  hasErrors,
  IProductForm,
  IProductFormErrors,
  productFormFromItem,
  rebaseAfterSave,
  rebaseOnRemote,
  TProductFormKey,
  validateProductForm,
} from '@/domain/catalog/product-form';
import { AlertType } from '@/interfaces/IAlert';
import { IItemWriteResponse } from '@/interfaces/ICatalogV2';
import { IItem } from '@/interfaces/IItem';
import { useCatalogPage } from './catalog-page-context';
import type { ICatalogEditorTarget } from './catalog-page-state';
import {
  TEditorExtraKey,
  useEditorExtras,
  useEditorPageFlags,
  useEditorUploads,
  useSavedFlash,
} from './editor-core';

/** A deep copy (old WebViews lack `structuredClone`). */
function copy<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

/**
 * The product editor (plan §5.12): the form and its base, dirty keys,
 * validation, remote changes (clean keys follow, dirty ones raise the
 * banner), and «Guardar» — conflict check, the partial PATCH (or the
 * create), then translations and stock. Instances are per target (the host
 * keys the editor by it), so nothing leaks between products.
 */
export function useProductEditor(target: ICatalogEditorTarget) {
  const { state, data, access, online, url, actions } = useCatalogPage();
  const ui = useUi();
  const confirmDialog = useConfirmDialog();
  const menuStore = useMenuStore();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const isNew = target.id === 'new';
  const itemId = typeof target.id === 'number' ? target.id : null;
  const restaurantId = data.restaurant.value?.id ?? null;
  const country = computed(() => data.rowContext.value.country);

  /** Unmounted: nothing may run after an await (Vue renderer safety). */
  let disposed = false;
  /** Closed, or another store is open: the result of an await is dropped. */
  function stale(): boolean {
    return disposed || (data.restaurant.value?.id ?? null) !== restaurantId;
  }
  /** Our last saved object: the store echo of our own write is not remote. */
  let ownItem: IItem | null = null;

  const item = computed<IItem | null>(() =>
    itemId !== null ? data.itemById.value[itemId] ?? null : null
  );
  const category = computed(() => {
    const id = form.value?.categoryId ?? item.value?.categoryId ?? null;
    return id !== null ? data.categoryById.value.get(id) ?? null : null;
  });

  const base = shallowRef<IProductForm | null>(null);
  const form = ref<IProductForm | null>(null);
  const saving = ref(false);
  const { savedFlash, flashSaved } = useSavedFlash();
  const submitted = ref(false);
  /** Bumped after «Crear y agregar otro»: «Nombre» takes the focus. */
  const focusNameSignal = ref(0);
  /** A dirty field changed on another device too. */
  const remoteChanged = ref(false);
  /**
   * Keys another device changed while they had unsaved edits here: the
   * rebase keeps the owner's value and «Guardar» asks before overwriting.
   */
  const conflictKeys = ref<TProductFormKey[]>([]);
  const failedExtras = ref<TEditorExtraKey[]>([]);
  const extras = useEditorExtras();
  const uploads = useEditorUploads();

  //#region Load

  /** Create: the valid `cId`, else the category in view, else the first one. */
  function createCategoryId(): number | null {
    const sections = data.sections.value;
    const ids = new Set(sections.map((section) => section.category.id));
    if (target.categoryId !== null && ids.has(target.categoryId))
      return target.categoryId;
    if (state.activeCategoryId !== null && ids.has(state.activeCategoryId))
      return state.activeCategoryId;
    return sections[0]?.category.id ?? null;
  }

  function resetCreateForm(categoryId: number, name = '') {
    base.value = emptyProductForm(categoryId);
    form.value = emptyProductForm(categoryId, name);
    submitted.value = false;
  }

  // A deep link may open before the catalog loads: the form waits for a category.
  if (isNew)
    watch(
      () => data.sections.value,
      () => {
        if (form.value) return;
        const categoryId = createCategoryId();
        if (categoryId !== null)
          resetCreateForm(categoryId, target.prefillName);
      },
      { immediate: true }
    );

  watch(
    item,
    (next) => {
      if (!next || isNew) return;
      if (!base.value || !form.value) {
        const loaded = productFormFromItem(next, country.value);
        ownItem = next;
        base.value = loaded;
        form.value = copy(loaded);
        return;
      }
      if (next === ownItem) return;
      const rebased = rebaseOnRemote(
        base.value,
        form.value,
        productFormFromItem(next, country.value)
      );
      base.value = rebased.base;
      form.value = rebased.form;
      if (rebased.conflicts.length) {
        remoteChanged.value = true;
        conflictKeys.value = [
          ...new Set([...conflictKeys.value, ...rebased.conflicts]),
        ];
      }
    },
    { immediate: true }
  );

  /** It was deleted here or on another device. */
  const deleted = computed(
    () =>
      !isNew && !!base.value && !item.value && data.loadStatus.value === 'ready'
  );

  //#endregion

  //#region State

  const dirtyKeys = computed<TProductFormKey[]>(() =>
    base.value && form.value ? changedKeys(base.value, form.value) : []
  );
  const changeCount = computed(
    () => dirtyKeys.value.length + extras.dirtyCount.value
  );
  const dirty = computed(() => changeCount.value > 0);

  const errors = computed<IProductFormErrors>(() =>
    form.value ? validateProductForm(form.value, isNew ? null : base.value) : {}
  );
  /** Errors show after the first save attempt. */
  const visibleErrors = computed<IProductFormErrors>(() =>
    submitted.value ? errors.value : {}
  );

  /** Role rules (§5.17): managers edit everything, others only availability. */
  const readOnly = computed(() => !access.value.canManage);
  const canEditAvailability = computed(() => access.value.canToggle);

  /** A shared group edited from here saved on its own (plan §5.13). */
  const groupsSaved = ref(false);
  useEditorPageFlags(
    state,
    dirty,
    () => form.value?.name ?? item.value?.name ?? '',
    () => groupsSaved.value
  );

  function update<K extends TProductFormKey>(key: K, value: IProductForm[K]) {
    if (!form.value) return;
    form.value = { ...form.value, [key]: value };
  }

  //#endregion

  //#region Save

  function fieldLabel(key: TProductFormKey): string {
    return t(`menuPage.editor.field.${key}`);
  }

  /** A dirty key another device changed: ask before overwriting it. */
  async function confirmConflicts(): Promise<boolean> {
    if (isNew) return true;
    const conflicts = dirtyKeys.value.filter((key) =>
      conflictKeys.value.includes(key)
    );
    if (!conflicts.length) return true;
    const accepted = await confirmDialog.confirm({
      title: t('menuPage.editor.conflictTitle'),
      message: t('menuPage.editor.conflictMessage', {
        fields: conflicts.map(fieldLabel).join(', '),
      }),
      acceptText: t('menuPage.editor.saveAnyway'),
    });
    if (!accepted && !stale()) remoteChanged.value = true;
    return accepted;
  }

  /** Saves the product; false when nothing was saved, null when nothing changed. */
  async function saveProduct(
    sent: IProductForm
  ): Promise<IItemWriteResponse | null | false> {
    if (!base.value || restaurantId === null) return false;
    if (isNew)
      return CatalogV2Service.createItem(
        createPayloadFromForm(sent, restaurantId)
      );
    const patch = diffProductForm(base.value, sent);
    if (!patch || itemId === null) return null;
    return CatalogV2Service.patchItem(itemId, patch);
  }

  /**
   * «Guardar» (or «Crear producto»); `another` keeps the create form open
   * for the next product. Resolves true when everything was saved.
   */
  async function save(options: { another?: boolean } = {}): Promise<boolean> {
    if (!form.value || !base.value || saving.value) return false;
    // «Guardar» includes the photos still being picked.
    if (uploads.busy()) {
      saving.value = true;
      await uploads.settled();
      if (stale()) return false;
      saving.value = false;
      if (!form.value || !base.value) return false;
    }
    submitted.value = true;
    if (hasErrors(errors.value) || extras.hasInvalid()) return false;
    if (!online.value) {
      ui.showToast(AlertType.Error, t('menuPage.errors.offline'));
      return false;
    }
    if (!(await confirmConflicts()) || stale()) return false;

    const sent = copy(form.value);
    const sentKeys = changedKeys(base.value, sent);
    // Keys not sent are clean: their earlier conflicts are settled.
    conflictKeys.value = conflictKeys.value.filter((key) =>
      sentKeys.includes(key)
    );
    saving.value = true;
    try {
      const response = await saveProduct(sent);
      const saved = response === false ? false : response?.item ?? null;
      // Into the store even if the editor closed meanwhile (the server has
      // it; the store skips another store's rows).
      if (saved) menuStore.upsertItems([saved], { own: true });
      if (stale() || saved === false) return false;
      let conflicts: TProductFormKey[] = [];
      if (response) {
        ownItem = response.item;
        actions.pin([response.item.id]);
        const savedForm = productFormFromItem(response.item, country.value);
        if (isNew) {
          // A create always ends clean: the editor then opens the product
          // without asking.
          form.value = copy(savedForm);
        } else if (form.value) {
          const previous = base.value;
          const rebased = rebaseAfterSave(
            form.value,
            sent,
            savedForm,
            response.created
          );
          form.value = rebased.form;
          conflicts = previous
            ? [
                ...rebased.conflicts,
                ...changedDuringSave(
                  previous,
                  savedForm,
                  rebased.form,
                  sentKeys
                ),
              ]
            : rebased.conflicts;
        }
        base.value = savedForm;
      }
      // The request's own echo can only flag the keys it sent: the answer
      // decides those. Other keys keep their realtime conflicts while they
      // are still dirty.
      conflictKeys.value = [
        ...new Set([
          ...conflicts,
          ...conflictKeys.value.filter(
            (key) => !sentKeys.includes(key) && dirtyKeys.value.includes(key)
          ),
        ]),
      ];
      remoteChanged.value = conflictKeys.value.length > 0;
      const savedId = saved ? saved.id : itemId;
      const failed =
        savedId !== null ? await extras.saveExtras(savedId, stale) : [];
      if (stale()) return false;
      failedExtras.value = failed;

      if (isNew && saved) {
        if (restaurantId !== null) actions.trackCreate(restaurantId);
        ui.showToast(AlertType.Success, t('menuPage.toast.productCreated'));
        if (options.another) {
          resetCreateForm(saved.categoryId);
          focusNameSignal.value += 1;
          return !failed.length;
        }
        // The page flags follow `dirty` (a pre-flush watcher) before the
        // route guard reads them.
        await nextTick();
        if (stale()) return false;
        url.replaceTarget('item', saved.id);
        return !failed.length;
      }
      if (!failed.length) {
        ui.showToast(AlertType.Success, t('menuPage.toast.productSaved'));
        flashSaved();
      }
      return !failed.length;
    } catch (error) {
      if (stale()) return false;
      if (catalogErrorCode(error) === 'CATALOG_STALE' && itemId !== null) {
        await data.refreshItems([itemId]);
        if (stale()) return false;
        if (item.value && form.value)
          form.value = detachMissingSizes(form.value, item.value);
        remoteChanged.value = true;
        ui.showToast(AlertType.Error, t('menuPage.editor.staleToast'));
      } else ui.serverError(error, t('menuPage.errors.save'));
      return false;
    } finally {
      if (!stale()) saving.value = false;
    }
  }

  /** «Guardar lo que falta»: only the parts that failed. */
  async function retryExtras(): Promise<boolean> {
    const savedId = item.value?.id ?? null;
    if (savedId === null || saving.value) return false;
    saving.value = true;
    try {
      const failed = await extras.saveExtras(savedId, stale);
      if (stale()) return false;
      failedExtras.value = failed;
      if (!failed.length) {
        ui.showToast(AlertType.Success, t('menuPage.toast.productSaved'));
        flashSaved();
      }
      return !failed.length;
    } finally {
      if (!stale()) saving.value = false;
    }
  }

  /** «Descartar»: back to what is saved. */
  function discard() {
    if (base.value) form.value = copy(base.value);
    extras.resetExtras();
    failedExtras.value = [];
    remoteChanged.value = false;
    conflictKeys.value = [];
    submitted.value = false;
  }

  //#endregion

  onScopeDispose(() => {
    disposed = true;
  });

  return {
    target,
    isNew,
    itemId,
    item,
    category,
    base,
    form,
    country,
    saving,
    savedFlash,
    submitted,
    focusNameSignal,
    remoteChanged,
    noteGroupSaved: () => {
      groupsSaved.value = true;
    },
    failedExtras,
    deleted,
    dirtyKeys,
    changeCount,
    dirty,
    errors,
    visibleErrors,
    readOnly,
    canEditAvailability,
    update,
    registerExtra: extras.registerExtra,
    trackUpload: uploads.track,
    save,
    retryExtras,
    discard,
  };
}

export type IProductEditor = ReturnType<typeof useProductEditor>;

const PRODUCT_EDITOR_KEY: InjectionKey<IProductEditor> = Symbol(
  'product-editor'
);

export function provideProductEditor(editor: IProductEditor): void {
  provide(PRODUCT_EDITOR_KEY, editor);
}

/** Throws outside the product editor. */
export function useProductEditorContext(): IProductEditor {
  const editor = inject(PRODUCT_EDITOR_KEY, null);
  if (!editor)
    throw new Error('useProductEditorContext() needs the product editor');
  return editor;
}
